/** Deterministic pseudo-randomness. Nothing here may depend on `Math.random`. */

/** FNV-1a over a string, used to turn names and prompts into seeds. */
export function hashString(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Mulberry32 — small, fast and good enough for procedural content. */
export function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return function next(): number {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Builds a shuffled 0..255 permutation table, doubled to avoid index wrapping. */
export function createPermutation(seed: number): Uint8Array {
  const random = createRandom(seed);
  const source = new Uint8Array(256);
  for (let i = 0; i < 256; i += 1) source[i] = i;
  for (let i = 255; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const tmp = source[i];
    source[i] = source[j];
    source[j] = tmp;
  }
  const table = new Uint8Array(512);
  table.set(source, 0);
  table.set(source, 256);
  return table;
}

/**
 * Deterministic hash of an integer lattice cell to `0 .. 1`.
 *
 * Lattice patterns (facets, bricks, scattered domes) need a stable per-cell
 * value without keeping a table around, and it has to stay stable across the
 * main thread and the export worker.
 */
export function hash2D(ix: number, iy: number, seed: number): number {
  let h = Math.imul(ix | 0, 0x27d4eb2d) ^ Math.imul(iy | 0, 0x165667b1) ^ (seed | 0);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}
